import { TreeList } from '@ibsys/kit';
import { useSortable } from '@dnd-kit/react/sortable';
import { OptimisticSortingPlugin } from '@dnd-kit/dom/sortable';
import { FolderIcon, PackageIcon } from 'lucide-react';
import { FC, useEffect, useRef } from 'react';
import { useHasPermission } from '@/entities/profile';
import { cn } from '@/shared/lib';
import { BlockPointsRuleMarks } from '../ExpertReportTree/BlockPointsRuleMarks';
import { useIsNodeCollapsed } from '../ExpertReportTree/expandedNodesContext';
import { KmNodeActions } from './KmNodeActions';
import { hasKmDescendant, type KmTreeNodeData } from './kmTreeUtils';

type KmStructureTreeNodeProps = {
  node: KmTreeNodeData;
  /** Порядковый номер узла среди узлов своего контейнера */
  index: number;
  /** Идентификатор контейнера сортировки — узел-заключение либо родительская группа */
  containerId: string;
  activeId: string;
  onSelect: (node: KmTreeNodeData) => void;
  onAddGroup: (parentGroupId: number) => void;
  onAddAssessment: (groupId: number) => void;
  onExpandNode: (nodeId: string) => void;
  isDesktop?: boolean;
};

const ICON_SIZE = 18;

/**
 * Строка дерева структуры заключения на модели знаний: группа (раскрываемая, если в ней
 * что-то есть) либо единица оценивания (всегда лист). Оптимистичная сортировка dnd-kit
 * отключена по той же причине, что и в дереве блоков: она переносит реальный DOM-узел между
 * контейнерами, и следующая перерисовка расходится с уже перенесённым узлом.
 */
export const KmStructureTreeNode: FC<KmStructureTreeNodeProps> = ({
  node,
  index,
  containerId,
  activeId,
  onSelect,
  onAddGroup,
  onAddAssessment,
  onExpandNode,
  isDesktop,
}) => {
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const isGroup = node.type === 'group';
  const hasChildren = Boolean(node.children?.length);
  const isCollapsed = useIsNodeCollapsed(node.id);

  // Пока группа свёрнута, отметки вложенных в неё групп не видны — она показывает отметку и о них
  const nestedAttention = isCollapsed ? node.nestedPointsRuleAttention : null;

  // Авто-раскрытие группы, внутри которой лежит выбранный элемент
  const isAutoExpandDone = useRef(false);
  useEffect(() => {
    if (isAutoExpandDone.current || !activeId || !node.children?.length) return;
    if (hasKmDescendant(node.children, activeId)) {
      onExpandNode(node.id);
    }
    isAutoExpandDone.current = true;
  }, [activeId, node.children, node.id, onExpandNode]);

  const { ref, handleRef, isDragging } = useSortable({
    id: node.id,
    index,
    group: containerId,
    disabled: !allowAdmin,
    plugins: (defaults) => defaults.filter((plugin) => plugin !== OptimisticSortingPlugin),
  });

  const isActive = activeId === node.id;

  const handleSelect = () => onSelect(node);
  const handleRowClick = () => {
    // На десктопе клик по строке открывает карточку, на узком экране — только раскрывает узел
    if (isDesktop) onSelect(node);
  };
  const handleAddGroup = () => onAddGroup(node.entityId);
  const handleAddAssessment = () => onAddAssessment(node.entityId);

  const icon = (
    <span
      className={cn(
        'mt-0.5 shrink-0',
        isActive ? 'text-accent-foreground' : 'text-muted-foreground group-hover/km-node:text-foreground',
      )}
    >
      {isGroup ? <FolderIcon size={ICON_SIZE} /> : <PackageIcon size={ICON_SIZE} />}
    </span>
  );

  const labelContent = (
    <>
      {icon}
      <span className="line-clamp-2 flex-1 text-left text-sm font-medium leading-tight">{node.label}</span>
      <BlockPointsRuleMarks
        ownerKind="group"
        hasCustomPointsRule={node.hasCustomPointsRule}
        attention={node.pointsRuleAttention}
        nestedAttention={nestedAttention}
      />
    </>
  );

  const actions = (
    <KmNodeActions
      isGroup={isGroup}
      onOpenCard={handleSelect}
      onAddGroup={handleAddGroup}
      onAddAssessment={handleAddAssessment}
      handleRef={handleRef}
      isDesktop={isDesktop}
      revealOnHover={isDesktop ? 'opacity-0 transition-opacity group-hover/km-node:opacity-100' : undefined}
    />
  );

  // Лист: единица оценивания и пустая группа
  if (!hasChildren) {
    return (
      <div ref={ref} className={cn(isDragging && 'opacity-50')}>
        <TreeList.Item value={node.id} onClick={handleSelect} className="group/km-node">
          <div className="flex flex-1 items-start gap-2">
            {labelContent}
            {actions}
          </div>
        </TreeList.Item>
      </div>
    );
  }

  return (
    <div ref={ref} className={cn(isDragging && 'opacity-50')}>
      <TreeList.Group
        value={node.id}
        classNames={{ header: 'group/km-node' }}
        label={<div className="flex flex-1 items-start gap-2">{labelContent}</div>}
        onClick={handleRowClick}
        rightSlot={actions}
      >
        <div className="relative">
          {node.children!.map((child, childIndex) => (
            <KmStructureTreeNode
              key={child.id}
              node={child}
              index={childIndex}
              containerId={node.id}
              activeId={activeId}
              onSelect={onSelect}
              onAddGroup={onAddGroup}
              onAddAssessment={onAddAssessment}
              onExpandNode={onExpandNode}
              isDesktop={isDesktop}
            />
          ))}
        </div>
      </TreeList.Group>
    </div>
  );
};
